import type { ComponentType, ReactNode } from "react";
import { MessageSquare, Clock, Mail, UserPlus, FileText, ShoppingCart } from "lucide-react";
import { InstagramIcon } from "@/components/InstagramIcon";
import type { AutomationStep, AutomationTrigger } from "@/types/Automation";

export const DEFAULT_PUBLIC_REPLIES = [
  "Sent! Message in your DMs 📤",
  "Thanks! Check your inbox ✉️",
  "We've sent you a DM! 📬",
];

export const TRIGGER_OPTIONS: {
  section: "instagram" | "engram";
  value: AutomationTrigger["type"];
  label: string;
  icon?: ReactNode;
}[] = [
  {
    section: "instagram",
    value: "instagram.comment",
    label: "User comments on post",
    icon: <InstagramIcon className="text-pink-600" />,
  },
  { section: "engram", value: "contacts.create", label: "Contact Created" },
];

export const STEP_OPTIONS_MAIN: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "page", label: "Page", icon: <FileText className="h-6 w-6" /> },
  { value: "checkout", label: "Checkout", icon: <ShoppingCart className="h-6 w-6" /> },
  { value: "createContact", label: "Create Contact", icon: <UserPlus className="h-6 w-6" /> },
  { value: "sms", label: "Send SMS", icon: <MessageSquare className="h-6 w-6" /> },
  { value: "instagram.sendMessage", label: "Send Message", icon: <InstagramIcon className="h-6 w-6 text-pink-600" /> },
  { value: "email", label: "Send Email", icon: <Mail className="h-6 w-6" /> },
  { value: "wait", label: "Delay", icon: <Clock className="h-6 w-6" /> },
];

export const STEP_OPTIONS_MAILERLITE: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "mailerlite.createSubscriber", label: "Create Subscriber", icon: <UserPlus className="h-6 w-6" /> },
];

export const STEP_OPTIONS_LAYLO: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "laylo.createSubscriber", label: "Create Subscriber", icon: <UserPlus className="h-6 w-6" /> },
];

export const STEP_OPTIONS_MANYCHAT: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "manychat.createSubscriber", label: "Create Subscriber", icon: <UserPlus className="h-6 w-6" /> },
];

export const STEP_OPTIONS_BEEHIIV: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "beehiiv.createSubscriber", label: "Create Subscriber", icon: <UserPlus className="h-6 w-6" /> },
];

export const STEP_OPTIONS_META: { value: AutomationStep["type"]; label: string; icon: ReactNode }[] = [
  { value: "meta.addToAudience", label: "Add to Audience", icon: <UserPlus className="h-6 w-6" /> },
];

export const STEP_STYLES: Record<
  AutomationStep["type"],
  { border: string; bg: string; text: string; handle: string; icon: ComponentType<{ className?: string }> }
> = {
  sms: { border: "border-blue-300", bg: "bg-blue-50", text: "text-blue-700", handle: "!bg-blue-400", icon: MessageSquare },
  "instagram.sendMessage": {
    border: "border-pink-300",
    bg: "bg-pink-50",
    text: "text-pink-700",
    handle: "!bg-pink-400",
    icon: InstagramIcon,
  },
  email: { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700", handle: "!bg-emerald-400", icon: Mail },
  emailCampaign: { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700", handle: "!bg-emerald-400", icon: Mail },
  wait: { border: "border-purple-300", bg: "bg-purple-50", text: "text-purple-700", handle: "!bg-purple-400", icon: Clock },
  "mailerlite.createSubscriber": {
    border: "border-green-300",
    bg: "bg-green-50",
    text: "text-green-800",
    handle: "!bg-green-500",
    icon: UserPlus,
  },
  "laylo.createSubscriber": {
    border: "border-violet-300",
    bg: "bg-violet-50",
    text: "text-violet-800",
    handle: "!bg-violet-500",
    icon: UserPlus,
  },
  "manychat.createSubscriber": {
    border: "border-amber-300",
    bg: "bg-amber-50",
    text: "text-amber-800",
    handle: "!bg-amber-500",
    icon: UserPlus,
  },
  "beehiiv.createSubscriber": {
    border: "border-orange-300",
    bg: "bg-orange-50",
    text: "text-orange-800",
    handle: "!bg-orange-500",
    icon: UserPlus,
  },
  "meta.addToAudience": {
    border: "border-blue-300",
    bg: "bg-blue-50",
    text: "text-blue-800",
    handle: "!bg-blue-500",
    icon: UserPlus,
  },
  page: {
    border: "border-indigo-300",
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    handle: "!bg-indigo-400",
    icon: FileText,
  },
  createContact: {
    border: "border-teal-300",
    bg: "bg-teal-50",
    text: "text-teal-700",
    handle: "!bg-teal-400",
    icon: UserPlus,
  },
  checkout: {
    border: "border-amber-300",
    bg: "bg-amber-50",
    text: "text-amber-800",
    handle: "!bg-amber-500",
    icon: ShoppingCart,
  },
};

export const STEP_LABELS: Record<AutomationStep["type"], string> = {
  sms: "Send SMS",
  "instagram.sendMessage": "Send Message",
  email: "Send Email",
  emailCampaign: "Send Email Campaign",
  wait: "Delay",
  "mailerlite.createSubscriber": "MailerLite · Create Subscriber",
  "laylo.createSubscriber": "Laylo · Create Subscriber",
  "manychat.createSubscriber": "Manychat · Create Subscriber",
  "beehiiv.createSubscriber": "Beehiiv · Create Subscriber",
  "meta.addToAudience": "Meta · Add to Audience",
  page: "Page",
  createContact: "Create Contact",
  checkout: "Checkout",
};

export const LAYLO_CONTACT_TYPE_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "emailAndPhone", label: "Email & Phone" },
] as const;

export const WAIT_UNIT_OPTIONS = [
  { value: "seconds", label: "Seconds" },
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

export const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export const STEP_FIELD_DEBOUNCE_MS = 200;
